import { API_PATHS, API_ROUTES } from '../../../lib/apiRoutes';
import { apiRequest, apiBlobRequest } from '../../../lib/api';
import { buildIdempotencyKey, buildStickyIdempotencyKey } from '../../../lib/idempotency';
import { triggerBlobDownload } from '../../../lib/downloadBlob';
import type { AdminCashPayoutListResponse, AdminCashPayoutSettleResponse } from '../types/cashPayouts.types';
import type { ApprovalReviewOptions } from '../../approvals/types/approvals.types';

export interface AdminDashboardOverview {
  startDate: string;
  endDate: string;
  timeZone: string;
  sales: {
    orderCount: number;
    grossCapturedCents: number;
    averageOrderValueCents: number;
    contributionCents: number;
    processingCostCents: number;
    driverPayoutCents: number;
  };
  orders: {
    totalOrders: number;
    completedOrders: number;
    completionRatePercent: number;
    statusCounts: Record<string, number>;
  };
  returns: {
    submissions: number;
    approvedSubmissions: number;
    approvedContainers: number;
    creditCents: number;
  };
  daily: Array<{
    date: string;
    orderCount: number;
    completedCount: number;
    grossCapturedCents: number;
  }>;
}

export type NotificationChannelId = 'email' | 'push' | 'sms';

export interface NotificationChannelStatus {
  configured: boolean;
  circuitOpen: boolean;
  failures24h: number;
}

export interface NotificationTelemetry {
  pending: number;
  failed: number;
  deadLettered: number;
  channels: Record<NotificationChannelId, NotificationChannelStatus>;
}

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 50;
const MAX_LIMIT = 100;

const normalizePage = (value = DEFAULT_PAGE): number =>
  Number.isFinite(value) && value > 0 ? Math.floor(value) : DEFAULT_PAGE;

const normalizeLimit = (value = DEFAULT_LIMIT): number => {
  if (!Number.isFinite(value)) return DEFAULT_LIMIT;
  return Math.min(MAX_LIMIT, Math.max(1, Math.floor(value)));
};

const normalizeId = (value: string, label: string): string => {
  const normalized = String(value ?? '').trim();
  if (!normalized) {
    throw new Error(`Invalid ${label}.`);
  }
  return encodeURIComponent(normalized);
};

const normalizeOptionalText = (value?: string): string | undefined => {
  const normalized = typeof value === 'string' ? value.trim() : '';
  return normalized ? normalized : undefined;
};

const normalizeRequiredText = (value: string, label: string): string => {
  const normalized = String(value ?? '').trim();
  if (!normalized) {
    throw new Error(`Invalid ${label}.`);
  }
  return normalized;
};

const normalizePayload = (payload: Record<string, unknown>): Record<string, unknown> => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new Error('Invalid payload.');
  }

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined)
  );
};

const APPROVAL_REVIEW_DECISIONS = new Set(['approved', 'rejected']);

const normalizeApprovalStatus = (value: string): 'approved' | 'rejected' => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!APPROVAL_REVIEW_DECISIONS.has(normalized)) {
    throw new Error('Invalid approval status.');
  }
  return normalized as 'approved' | 'rejected';
};

type adminApiEnvelope<T = unknown> = {
  success?: boolean;
  data?: T;
  message?: string;
  traceId?: string;
  settings?: Record<string, unknown>;
  stores?: Array<Record<string, unknown>>;
  product?: Record<string, unknown>;
  ledger?: Array<Record<string, unknown>>;
  [key: string]: unknown;
};

const LIST_STATUSES = new Set(['pending', 'approved', 'rejected']);

const buildListQuery = (page?: number, limit?: number, status?: string): string => {
  const params = new URLSearchParams({
    page: String(normalizePage(page)),
    limit: String(normalizeLimit(limit))
  });
  const normalizedStatus = status?.trim().toLowerCase();
  if (normalizedStatus && LIST_STATUSES.has(normalizedStatus)) {
    params.set('status', normalizedStatus);
  }
  return params.toString();
};

export const adminApi = {
  listAdminOrders: (page = DEFAULT_PAGE, limit = DEFAULT_LIMIT) =>
    apiRequest<adminApiEnvelope>(`${API_ROUTES.v1.admin}/orders?${buildListQuery(page, limit)}`),
  updateAdminOrderStatus: (orderId: string, status: string, reason: string) =>
    apiRequest<adminApiEnvelope>(`${API_ROUTES.v1.admin}/orders/${normalizeId(orderId, 'order id')}/status`, {
      method: 'PATCH',
      body: JSON.stringify({
        status: normalizeRequiredText(status, 'order status'),
        reason: normalizeRequiredText(reason, 'reason')
      })
    }),
  /** Clears a login lockout. Each click is a new unlock, so each gets its own key. */
  unlockUser: (userId: string, reason: string) =>
    apiRequest<adminApiEnvelope>(API_PATHS.admin.unlockUser(normalizeRequiredText(userId, 'user id')), {
      method: 'POST',
      body: JSON.stringify({ reason: normalizeRequiredText(reason, 'unlock reason') }),
      idempotencyKey: buildIdempotencyKey('admin-user-unlock', [userId]),
      requireIdempotencyKey: true
    }),
  /** A tier can legitimately be set A → B → A, so each click gets its own key. */
  setUserTier: (userId: string, tier: string, reason: string) =>
    apiRequest<adminApiEnvelope>(`${API_ROUTES.v1.admin}/users/${normalizeId(userId, 'user id')}/tier`, {
      method: 'POST',
      body: JSON.stringify({
        tier: normalizeRequiredText(tier, 'tier'),
        reason: normalizeRequiredText(reason, 'tier change reason')
      }),
      idempotencyKey: buildIdempotencyKey('admin-user-tier', [userId, tier]),
      requireIdempotencyKey: true
    }),
  listAdminProducts: (page = DEFAULT_PAGE, limit = DEFAULT_LIMIT) =>
    apiRequest<adminApiEnvelope>(`${API_ROUTES.v1.admin}/products?${buildListQuery(page, limit)}`),
  updateAdminProduct: (productId: string, payload: Record<string, unknown>) =>
    apiRequest<adminApiEnvelope>(`${API_ROUTES.v1.admin}/products/${normalizeId(productId, 'productId')}`, {
      method: 'PATCH',
      body: JSON.stringify(normalizePayload(payload))
    }),
  listAdminReturns: (page = DEFAULT_PAGE, limit = DEFAULT_LIMIT, status?: string) =>
    apiRequest<adminApiEnvelope>(`${API_ROUTES.v1.admin}/bottleReturns?${buildListQuery(page, limit, status)}`),
  listAdminCashPayouts: (page = DEFAULT_PAGE, limit = DEFAULT_LIMIT, startDate?: string, endDate?: string) => {
    const params = new URLSearchParams({
      page: String(normalizePage(page)),
      limit: String(normalizeLimit(limit))
    });
    const normalizedStart = normalizeOptionalText(startDate);
    const normalizedEnd = normalizeOptionalText(endDate);
    if (normalizedStart) params.set('startDate', normalizedStart);
    if (normalizedEnd) params.set('endDate', normalizedEnd);
    return apiRequest<AdminCashPayoutListResponse>(`${API_ROUTES.v1.admin}/cashPayouts?${params.toString()}`);
  },
  settleAdminCashPayout: (payoutId: string, note?: string) =>
    apiRequest<AdminCashPayoutSettleResponse>(`${API_ROUTES.v1.admin}/cashPayouts/${normalizeId(payoutId, 'cash payout id')}/settle`, {
      method: 'PATCH',
      body: JSON.stringify({ note: normalizeOptionalText(note) }),
      idempotencyKey: buildIdempotencyKey('admin-cash-payout-settle', [payoutId]),
      requireIdempotencyKey: true
    }),

  getAdminEarningsSummary: () =>
    apiRequest<{
      success: boolean;
      data: {
        availableBalanceCents: number;
        lifetimeEarningsCents: number;
        pendingCashoutCents: number;
        stripeConnected: boolean;
        stripeAccountStatus?: string;
        recentEntries: Array<{
          id: string;
          amountCents: number;
          type: string;
          status: string;
          description: string;
          createdAt: string;
        }>;
      };
    }>(`${API_ROUTES.v1.admin}/payouts/summary`),

  requestAdminCashout: () =>
    apiRequest<{ success: boolean; amountCents: number; transferId?: string }>(`${API_ROUTES.v1.admin}/payouts/cashout`, {
      method: 'POST',
      idempotencyKey: buildIdempotencyKey('admin-contractor-cashout'),
      requireIdempotencyKey: true
    }),

  getAdminOnboardingLink: () =>
    apiRequest<{ success: boolean; clientSecret: string; accountId?: string }>(`${API_ROUTES.v1.admin}/payouts/onboarding`, {
      method: 'POST',
      idempotencyKey: buildIdempotencyKey('admin-contractor-onboarding'),
      requireIdempotencyKey: true
    }),
  listFraudCases: () =>
    apiRequest<adminApiEnvelope>(`${API_ROUTES.v1.fraud}/cases`),
  decideFraudCase: (id: string, action: 'hold' | 'release' | 'deny', rationale: string) =>
    apiRequest<adminApiEnvelope>(`${API_ROUTES.v1.fraud}/cases/${normalizeId(id, 'fraud case id')}/decision`, {
      method: 'POST',
      body: JSON.stringify({
        action,
        rationale: normalizeRequiredText(rationale, 'rationale')
      }),
      idempotencyKey: buildStickyIdempotencyKey('admin-fraud-decide', [id, action]),
      requireIdempotencyKey: true
    }),

  /**
   * One decision click = one key. Pass the same `idempotencyKey` when retrying the same
   * click after a step-up challenge (challenges are never sealed by the server).
   */
  reviewApproval: (id: string, status: 'approved' | 'rejected', options: ApprovalReviewOptions = {}) =>
    apiRequest<adminApiEnvelope>(API_PATHS.approvals.review(normalizeId(id, 'approval id')), {
      method: 'POST',
      body: JSON.stringify({
        status: normalizeApprovalStatus(status),
        ...(normalizeOptionalText(options.note) ? { note: normalizeOptionalText(options.note) } : {}),
        ...(normalizeOptionalText(options.expectedEntityState)
          ? { expectedEntityState: normalizeOptionalText(options.expectedEntityState) }
          : {})
      }),
      headers: options.stepUpToken ? { 'X-Step-Up-Token': options.stepUpToken } : undefined,
      idempotencyKey: options.idempotencyKey || buildIdempotencyKey('admin-approval-review', [id, status]),
      requireIdempotencyKey: true
    }),
  saveSettings: (payload: Record<string, unknown>) =>
    apiRequest<adminApiEnvelope>(API_ROUTES.v1.settings, {
      method: 'PUT',
      body: JSON.stringify(normalizePayload(payload)),
      idempotencyKey: buildIdempotencyKey('admin-settings-save'),
      requireIdempotencyKey: true
    }),
  createStorePrice: (storeId: string, payload: Record<string, unknown>) =>
    apiRequest<adminApiEnvelope>(`${API_ROUTES.v1.stores}/${normalizeId(storeId, 'store id')}/prices`, {
      method: 'POST',
      body: JSON.stringify(normalizePayload(payload))
    }),
  updateProduct: (id: string, payload: Record<string, unknown>) =>
    apiRequest<adminApiEnvelope>(`${API_ROUTES.v1.products}/${normalizeId(id, 'productId')}`, {
      method: 'PATCH',
      body: JSON.stringify(normalizePayload(payload))
    }),
  deleteProduct: (id: string) =>
    apiRequest<adminApiEnvelope>(`${API_ROUTES.v1.products}/${normalizeId(id, 'productId')}`, {
      method: 'DELETE'
    }),
  getUserLedger: (userId: string) => apiRequest<adminApiEnvelope>(API_PATHS.users.ledger(normalizeId(userId, 'user id'))),
  getUserCart: (userId: string) => apiRequest<adminApiEnvelope>(API_PATHS.users.cart(normalizeId(userId, 'user id'))),
  getDashboardOverview: (range: { startDate: string; endDate: string }) =>
    apiRequest<{ success: boolean; data: AdminDashboardOverview }>(API_PATHS.admin.analyticsOverview, {
      query: range
    }),
  getNotificationTelemetry: () =>
    apiRequest<{ success: boolean; data: NotificationTelemetry }>(API_PATHS.notifications.telemetry),
  getAgentProtocolsStatus: () =>
    apiRequest<{
      success?: boolean;
      data?: {
        generatedAt?: string;
        protocols?: Array<{
          id: string;
          name: string;
          status: 'ready' | 'degraded' | 'unavailable';
          detail: string;
          endpoints?: string[];
        }>;
      };
    }>(API_PATHS.ai.protocolsStatus),

  // K1: start an admin impersonation session against a target user with a
  // mandatory free-text reason. The reason is recorded in the audit log on
  // the backend; this layer just normalizes / validates inputs.
  startImpersonation: (targetUserId: string, reason: string) =>
    apiRequest<adminApiEnvelope>(API_PATHS.admin.impersonationStart, {
      method: 'POST',
      body: JSON.stringify({
        targetUserId: normalizeRequiredText(targetUserId, 'target user id'),
        reason: normalizeRequiredText(reason, 'impersonation reason')
      }),
      idempotencyKey: buildIdempotencyKey('admin-impersonation-start', [targetUserId]),
      requireIdempotencyKey: true
    }),
  stopImpersonation: () =>
    apiRequest<adminApiEnvelope>(API_PATHS.admin.impersonationStop, { method: 'POST' }),
  getImpersonationStatus: () =>
    apiRequest<adminApiEnvelope>(API_PATHS.admin.impersonationStatus),

  // K2: D2 admin order-intervention surface.
  // The backend exposes three idempotent admin endpoints — address
  // correction, item removal, and substitution override. We expose them
  // as named methods so the admin order panel doesn't have to know the
  // URL shape and can stay focused on UX (forms, confirmations, audit).
  correctOrderShippingAddress: (
    orderId: string,
    address: { line1?: string; line2?: string; city?: string; state?: string; zip?: string; country?: string },
    reason: string
  ) =>
    apiRequest<adminApiEnvelope>(`${API_ROUTES.v1.admin}/orders/${normalizeId(orderId, 'order id')}/shippingAddress`, {
      method: 'PATCH',
      body: JSON.stringify({
        shippingAddress: normalizePayload(
          Object.fromEntries(
            Object.entries(address)
              .map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
              .filter(([, value]) => value !== '')
          )
        ),
        reason: normalizeRequiredText(reason, 'reason')
      }),
      idempotencyKey: buildStickyIdempotencyKey('admin-order-address', [orderId, reason]),
      requireIdempotencyKey: true
    }),
  removeOrderItem: (orderId: string, productId: string, reason: string) =>
    apiRequest<adminApiEnvelope>(`${API_ROUTES.v1.admin}/orders/${normalizeId(orderId, 'order id')}/items`, {
      method: 'DELETE',
      body: JSON.stringify({
        productId: normalizeRequiredText(productId, 'productId'),
        reason: normalizeRequiredText(reason, 'reason')
      }),
      idempotencyKey: buildStickyIdempotencyKey('admin-order-item-remove', [orderId, productId]),
      requireIdempotencyKey: true
    }),
  overrideOrderSubstitution: (
    orderId: string,
    payload: { fromProductId: string; toProductId: string; reason: string }
  ) =>
    apiRequest<adminApiEnvelope>(`${API_ROUTES.v1.admin}/orders/${normalizeId(orderId, 'order id')}/substitution`, {
      method: 'POST',
      body: JSON.stringify({
        fromProductId: normalizeRequiredText(payload.fromProductId, 'fromProductId'),
        toProductId: normalizeRequiredText(payload.toProductId, 'toProductId'),
        reason: normalizeRequiredText(payload.reason, 'reason')
      }),
      idempotencyKey: buildStickyIdempotencyKey('admin-order-sub', [
        orderId,
        payload.fromProductId,
        payload.toProductId
      ]),
      requireIdempotencyKey: true
    }),

  // K3: admin driver onboarding controls. One click = one key.
  transitionDriverOnboarding: (driverId: string, nextState: string, note?: string) =>
    apiRequest<adminApiEnvelope>(`${API_ROUTES.v1.admin}/drivers/${normalizeId(driverId, 'driver id')}/onboarding/transition`, {
      method: 'POST',
      body: JSON.stringify({
        nextState: normalizeRequiredText(nextState, 'next state'),
        note: normalizeOptionalText(note)
      }),
      idempotencyKey: buildIdempotencyKey('admin-driver-onboarding-transition', [driverId, nextState]),
      requireIdempotencyKey: true
    }),
  updateDriverOnboardingFlags: (
    driverId: string,
    flags: Record<string, boolean | string | null>
  ) =>
    apiRequest<adminApiEnvelope>(`${API_ROUTES.v1.admin}/drivers/${normalizeId(driverId, 'driver id')}/onboarding/flags`, {
      method: 'PATCH',
      body: JSON.stringify({ flags: normalizePayload(flags as Record<string, unknown>) }),
      idempotencyKey: buildIdempotencyKey('admin-driver-onboarding-flags', [driverId]),
      requireIdempotencyKey: true
    }),

  // K5: federated support search (D5).
  supportSearch: (query: string, limit = 25) =>
    apiRequest<adminApiEnvelope>(`${API_ROUTES.v1.admin}/support/search?q=${encodeURIComponent(normalizeRequiredText(query, 'query'))}&limit=${normalizeLimit(limit)}`),

  // K4: weekly driver payout run. Approval rides through the existing
  // approvals workflow (approvals.review) — there is no separate
  // /cash-payouts/:id/approve endpoint and we shouldn't fabricate one.
  // Dry runs move no money, so every click previews fresh numbers; a live run is
  // sticky per runKey so the same batch can never be submitted twice.
  runWeeklyPayoutBatch: (payload: { dryRun?: boolean; runKey?: string; note?: string } = {}) => {
    const runKey = payload.runKey || new Date().toISOString().slice(0, 10);
    return apiRequest<adminApiEnvelope>(`${API_ROUTES.v1.admin}/driverPayouts/run`, {
      method: 'POST',
      body: JSON.stringify(normalizePayload(payload as Record<string, unknown>)),
      idempotencyKey: payload.dryRun
        ? buildIdempotencyKey('driver-payout-run', [runKey, 'dry'])
        : buildStickyIdempotencyKey('driver-payout-run', [runKey, 'live']),
      requireIdempotencyKey: true
    });
  },

  // Certified Dispute & Evidence Dossier PDF
  downloadOrderDossier: async (orderId: string): Promise<Blob> => {
    const normalized = normalizeId(orderId, 'order id');
    return await apiBlobRequest(`${API_ROUTES.v1.admin}/orders/${normalized}/dossier`);
  },

  // Certified Customer Investigation Dossier PDF
  downloadCustomerDossier: async (userId: string): Promise<Blob> => {
    const normalized = normalizeId(userId, 'user id');
    return await apiBlobRequest(`${API_ROUTES.v1.admin}/users/${normalized}/dossier`);
  },

  // 1-Click Direct Stripe Dispute Evidence Submission
  submitDisputeEvidenceToStripe: (orderId: string) =>
    apiRequest<adminApiEnvelope<{ message?: string }>>(`${API_ROUTES.v1.admin}/orders/${normalizeId(orderId, 'order id')}/dispute/submit-evidence`, {
      method: 'POST',
      idempotencyKey: buildStickyIdempotencyKey('admin-dispute-submit-stripe', [orderId]),
      requireIdempotencyKey: true
    })
};

export const triggerDossierDownload = (blob: Blob, orderId: string) =>
  triggerBlobDownload(blob, `ninpo-dossier-${orderId}.pdf`);

export const triggerCustomerDossierDownload = (blob: Blob, userId: string) =>
  triggerBlobDownload(blob, `ninpo-customer-dossier-${userId}.pdf`);



